import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Eye, Library, LogOut, Search, Calendar, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import SpEdKitLogo from '../components/SpEdKitLogo';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MyStories = ({ user }) => {
  const navigate = useNavigate();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [storyToDelete, setStoryToDelete] = useState(null);

  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      const response = await axios.get(`${API}/stories`, {
        withCredentials: true
      });
      setStories(response.data);
    } catch (error) {
      console.error('Error fetching stories:', error);
      toast.error('Failed to load stories');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!storyToDelete) return;
    
    try {
      await axios.delete(`${API}/stories/${storyToDelete.story_id}`, {
        withCredentials: true
      });
      setStories(stories.filter(s => s.story_id !== storyToDelete.story_id));
      toast.success('Story deleted');
    } catch (error) {
      console.error('Error deleting story:', error);
      toast.error('Failed to delete story');
    } finally {
      setDeleteDialogOpen(false);
      setStoryToDelete(null);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate('/');
    } catch (error) {
      toast.error('Failed to logout');
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const filteredStories = stories.filter(story => {
    const query = searchQuery.toLowerCase();
    return (
      story.title?.toLowerCase().includes(query) ||
      story.student_info?.name?.toLowerCase().includes(query) ||
      story.scenario?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="min-h-screen bg-background" data-testid="my-stories-page">
      {/* Header */}
      <header className="w-full px-6 py-4 bg-white shadow-soft">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="cursor-pointer" onClick={() => navigate('/dashboard')}>
            <SpEdKitLogo className="w-10 h-10" textClassName="text-2xl" />
          </div>
          
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              className="hidden sm:flex items-center gap-2 text-text-secondary hover:text-primary hover:bg-primary/10"
              data-testid="dashboard-nav-btn"
            >
              Dashboard
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-10 w-10 rounded-full" data-testid="user-menu-btn">
                  <Avatar className="h-10 w-10 border-2 border-primary/20">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-primary/10 text-primary font-heading">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end">
                <div className="px-3 py-2">
                  <p className="font-heading font-semibold text-text-primary">{user?.name}</p>
                  <p className="text-sm text-text-muted truncate">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/dashboard')} className="cursor-pointer">
                  <Library className="mr-2 h-4 w-4" />
                  Dashboard
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive" data-testid="logout-btn">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-6 py-12 max-w-7xl mx-auto">
        {/* Title & Actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="font-heading font-bold text-3xl text-text-primary mb-1">
              My Stories
            </h1>
            <p className="font-body text-text-secondary">
              {stories.length} {stories.length === 1 ? 'story' : 'stories'} in your library
            </p>
          </div>
          
          <Button
            onClick={() => navigate('/create')}
            className="bg-primary text-white hover:bg-primary/90 rounded-full px-6 py-2 shadow-soft"
            data-testid="create-new-story-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Create New Story
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-8 max-w-md">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-muted" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search stories..."
            className="pl-12 border-2 border-primary/20 rounded-xl focus:border-primary"
            data-testid="search-input"
          />
        </div>

        {/* Stories Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-card animate-pulse">
                <div className="w-full h-40 bg-secondary/50 rounded-xl mb-4"></div>
                <div className="h-5 bg-secondary/50 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-secondary/50 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : filteredStories.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredStories.map((story) => (
              <div 
                key={story.story_id}
                className="bg-white rounded-2xl shadow-card hover:shadow-float transition-all duration-300 overflow-hidden group"
                data-testid={`story-card-${story.story_id}`}
              >
                {/* Card Image/Cover */}
                <div 
                  className="w-full h-40 bg-gradient-to-br from-primary/10 to-secondary flex items-center justify-center cursor-pointer"
                  onClick={() => navigate(`/story/${story.story_id}`)}
                >
                  {story.pages?.[0]?.image_base64 ? (
                    <img 
                      src={story.pages[0].image_base64} 
                      alt={story.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <BookOpen className="w-16 h-16 text-primary/30" />
                  )}
                </div>
                
                {/* Card Content */}
                <div className="p-6">
                  <h3 
                    className="font-heading font-semibold text-lg text-text-primary mb-1 line-clamp-1 cursor-pointer hover:text-primary transition-colors"
                    onClick={() => navigate(`/story/${story.story_id}`)}
                  >
                    {story.title}
                  </h3>
                  <p className="font-body text-sm text-text-muted mb-3">
                    For {story.student_info?.name}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-text-muted text-xs">
                      <Calendar className="w-3 h-3" />
                      <span>{formatDate(story.created_at)}</span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/story/${story.story_id}`)}
                        className="text-primary hover:bg-primary/10 rounded-full p-2"
                        data-testid={`view-story-${story.story_id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setStoryToDelete(story);
                          setDeleteDialogOpen(true);
                        }}
                        className="text-destructive hover:bg-destructive/10 rounded-full p-2"
                        data-testid={`delete-story-${story.story_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-12 shadow-card text-center" data-testid="no-stories-message">
            <div className="w-20 h-20 bg-secondary/50 rounded-full flex items-center justify-center mx-auto mb-6">
              <BookOpen className="w-10 h-10 text-primary/50" />
            </div>
            {searchQuery ? (
              <>
                <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
                  No stories found
                </h3>
                <p className="font-body text-text-secondary mb-6">
                  No stories match your search "{searchQuery}"
                </p>
                <Button
                  onClick={() => setSearchQuery('')}
                  variant="outline"
                  className="border-2 border-primary text-primary hover:bg-primary/10 rounded-full"
                >
                  Clear Search
                </Button>
              </>
            ) : (
              <>
                <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
                  No stories yet
                </h3>
                <p className="font-body text-text-secondary mb-6">
                  Create your first social story to help a child navigate their world.
                </p>
                <Button
                  onClick={() => navigate('/create')}
                  className="bg-primary text-white hover:bg-primary/90 rounded-full px-8 py-3"
                  data-testid="create-first-story-btn"
                >
                  Create Your First Story
                </Button>
              </>
            )}
          </div>
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete this story?</AlertDialogTitle>
            <AlertDialogDescription className="font-body">
              This will permanently delete "{storyToDelete?.title}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-full"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MyStories;
